import type { ScreenContext } from './types.ts';
import { createMoodSlider } from '@/components/mood-slider.ts';
import { createNoteInput } from '@/components/note-input.ts';
import { createContinueButton, setButtonLoading } from '@/components/continue-button.ts';
import { createSession } from '@/api/operations.ts';
import { getCurrentUserId, initAuth } from '@/firebase.ts';

export function renderBaseline(container: HTMLElement, { state, dispatch }: ScreenContext) {
  const startedAt = performance.now();
  container.innerHTML = `
    <div class="card space-y-6">
      <div>
        <p class="text-sm uppercase tracking-wide text-gray-500">Step 2</p>
        <h2 class="text-2xl font-semibold text-gray-900">How do you feel right now?</h2>
        <p class="text-gray-700 mt-1">This sets your baseline for the session.</p>
      </div>
      <div id="slider-slot"></div>
      <div id="note-slot"></div>
      <div id="action-slot"></div>
    </div>
  `;

  const slider = createMoodSlider('baseline', 'Baseline stress level', state.baselineMood ?? 5,true);
  container.querySelector('#slider-slot')?.appendChild(slider.root);

  const note = createNoteInput('baseline-note');
  container.querySelector('#note-slot')?.appendChild(note.root);

  const btn = createContinueButton('Start Listening');
  container.querySelector('#action-slot')?.appendChild(btn);

  btn.addEventListener('click', async () => {
    setButtonLoading(btn, true, 'Start Listening', 'Saving...');
    try {
      const uid = (await initAuth()) ?? getCurrentUserId();
      if (!uid) {
        alert('Anonymous sign-in is required before starting. Please enable Anonymous Auth in Firebase and reload.');
        return;
      }

      const baselineDurationMs = Math.round(performance.now() - startedAt);
      dispatch({
        type: 'SET_BASELINE',
        payload: { mood: slider.getValue(), note: note.getValue() },
      });

      // Create session in backend (best-effort)
      const session = await createSession({
        activity: state.context?.activity ?? 'unknown',
        energyLevel: state.context?.energy ?? 0,
        stressLevel: state.context?.stress ?? 0,
        baselineMood: slider.getValue(),
        baselineNote: note.getValue(),
        deviceId: uid,
        contextDurationMs: state.context?.durationMs,
        baselineDurationMs,
      });

      dispatch({ type: 'SET_SESSION_ID', payload: session.id });
      dispatch({ type: 'NEXT_PHASE' });
    } catch (error) {
      console.error('Failed to create session', error);
      alert('Could not start session because writing to Realtime Database was denied. Check Realtime Database rules and anonymous auth.');
    } finally {
      setButtonLoading(btn, false, 'Start Listening');
    }
  });
}
